/**
 * CopyUrlTrackingPanel — "where will these clicks actually land?", on step 2.
 *
 * Google assembles a landing page URL from three layers that live in three
 * different places (the final URL, the tracking template, the final URL suffix),
 * and the copy flow now changes all three. Nobody should have to run that
 * assembly in their head to find out whether a copy will report as itself.
 *
 * So the BUILD does it (campaign_editor/copy_url_tracking.py) and this renders
 * the answer: one row per distinct landing URL, the resolved click destination,
 * what the identity rewrite changed, and any conflict found assembling it.
 *
 * Two things it exists to make impossible to miss:
 *
 *   1. WHAT THE REWRITE DID. A copied URL carrying the SOURCE campaign's name or
 *      id is rewritten so the copy reports as itself. That is a change to the
 *      advertiser's own URL, so it is shown as before -> after per parameter,
 *      never applied silently.
 *   2. A TRACKING TEMPLATE THAT WILL BREAK — a query starting with "&", or a
 *      template with no {lpurl}. Two "?" is deliberately not flagged: IDFC's
 *      sources run that shape and the copy reproduces it (see copyUrlPreview).
 *
 * ALL URL WORK LIVES HERE. Step 1 used to carry the campaign's tracking template
 * and final URL suffix and a pair of apply-to-every-sitelink boxes, but step 1
 * runs BEFORE the build: it cannot list the sitelinks, and it cannot show what
 * any of those values produce. That is how a template which collided with its own
 * sitelink URLs shipped twice. Here the fields and the URL they assemble are on
 * screen together, so a mistake is visible while it is being made:
 *
 *   - the campaign's tracking template + final URL suffix (EditableCampaignUrls),
 *     which tag every ad and every sitelink that has none of its own;
 *   - each sitelink's text, final URL and own tracking pair (EditableSitelink),
 *     with add and remove.
 *
 * Read-only without the handlers — a DV360/TTD copy, or an older build.
 */
import { useEffect, useState } from 'react';
import { previewLandingUrl } from './copyUrlPreview';

// The plan is keyed by SOURCE campaign id (a merged shell copies N campaigns and
// each contributes its own entry) — see copy_url_tracking.build_url_tracking_plan.
// Rendered flat, with the campaign named on each group, because one copy of one
// campaign is the overwhelmingly common case and a single group needs no heading
// hierarchy above it.
function planGroups(plan) {
  if (!plan || typeof plan !== 'object' || Array.isArray(plan)) return [];
  return Object.entries(plan)
    .map(([sourceId, group]) => ({ sourceId, ...(group || {}) }))
    .filter((g) => Array.isArray(g.campaign) || Array.isArray(g.sitelinks));
}

// ── Editable sitelinks ────────────────────────────────────────────────────
// Sitelinks are CAMPAIGN-level on every Google Ads type — verified across this
// account: 2,097 links over 152 campaigns, and zero at asset-group, ad-group or
// account level. So one list per campaign is the whole model; there is no
// per-asset-group list to edit.
//
// Rows render from the LIVE template rather than from the build-time plan, so an
// edit shows immediately instead of waiting for a rebuild. The plan is still the
// source for what the identity rewrite changed (matched by link text).
const SITELINK_INPUTS = [
  ['link_text', 'Link text', 'e.g. Open NRE Account'],
  ['final_url', 'Final URL', 'https://… (required — without it the link is dropped at launch)'],
  ['tracking_url_template', 'Tracking template', "blank = use the campaign's"],
  ['final_url_suffix', 'Final URL suffix', "blank = use the campaign's"],
];

function EditableSitelink({ sitelink, campaignTemplate, campaignSuffix, planEntry, onOp, onRemove }) {
  const [draft, setDraft] = useState(sitelink);
  // The committed values are the identity: a re-render from a saved edit should
  // adopt them, but typing must not be clobbered mid-keystroke.
  const [dirty, setDirty] = useState(false);
  // Armed by the ×, cleared by Cancel or by the removal itself. Row-local, so two
  // rows can never be armed at once and arming one does not disturb the other.
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (!dirty) setDraft(sitelink); }, [sitelink, dirty]);

  const field = (k) => draft?.[k] ?? '';
  const set = (k, v) => { setDirty(true); setDraft((d) => ({ ...(d || {}), [k]: v })); };

  // A sitelink's own tracking overrides the campaign's — so the preview must fall
  // back to the campaign's values exactly as Google will.
  const { issues } = previewLandingUrl(
    field('final_url'),
    field('tracking_url_template') || campaignTemplate,
    field('final_url_suffix') || campaignSuffix,
  );
  // Every way create_sitelink_assets DROPS a sitelink, checked here because it
  // drops them by appending to an error list and continuing — the launch
  // succeeds, the sitelink is simply absent, and nothing downstream says which
  // one or why. Limits are that function's: link text 25, descriptions 35.
  const missingUrl = !String(field('final_url')).trim();
  const tooLong = [
    { key: 'link_text', label: 'link text', max: 25 },
    { key: 'description1', label: 'description 1', max: 35 },
    { key: 'description2', label: 'description 2', max: 35 },
  ].map((c) => ({ ...c, length: String(field(c.key)).trim().length }))
    .filter((c) => c.length > c.max);
  // Which inputs to mark: the one missing a required value, and any over-length one.
  const flagged = new Set([
    ...(missingUrl ? ['final_url'] : []),
    ...tooLong.map((c) => c.key),
  ]);

  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    const original = sitelink?.link_text || '';
    const text = String(field('link_text')).trim();
    if (!text) { setDraft(sitelink); return; }
    onOp({
      op: 'edit',
      value: original,
      ...(text !== original ? { newValue: text } : {}),
      fields: {
        final_url: field('final_url'),
        tracking_url_template: field('tracking_url_template'),
        final_url_suffix: field('final_url_suffix'),
      },
    });
  };

  return (
    <div className={`cw-url-row cw-sl-edit${issues.length || missingUrl || tooLong.length ? ' has-issue' : ''}`}>
      <div className="cw-url-head">
        <span className="cw-url-label">{sitelink?.link_text || 'New sitelink'}</span>
        <div className="gap" style={{ flex: 1 }} />
        {/* TWO-STEP, because a remove here is not a staged change: this grid saves
            one edit per commit, so the × writes the template and PUTs it straight
            away. There is no undo and no "discard changes" to fall back on, and
            the row — with its URL and tracking — is gone from the panel the moment
            it is clicked. An inline confirm rather than window.confirm so it can
            name the link, say what happens, and be testable. */}
        {confirming ? (
          <span className="cw-sl-confirm">
            <span className="cw-sl-confirm-q">Remove this sitelink from the copy?</span>
            <button
              type="button"
              className="cw-btn cw-btn-secondary cw-btn-sm"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="cw-btn cw-btn-sm cw-sl-confirm-yes"
              onClick={() => { setConfirming(false); onRemove(); }}
            >
              Remove
            </button>
          </span>
        ) : (
          <button type="button" className="cw-sl-remove" onClick={() => setConfirming(true)}
            aria-label={`Remove sitelink ${sitelink?.link_text || ''}`}>
            <span className="material-icons">close</span>
          </button>
        )}
      </div>

      {SITELINK_INPUTS.map(([k, label, placeholder]) => (
        <div className="cw-url-field" key={k}>
          <span className="cw-url-key">{label}</span>
          <input
            className={`cw-url-val cw-url-in${flagged.has(k) ? ' cw-sl-missing' : ''}`}
            value={field(k)}
            placeholder={placeholder}
            onChange={(e) => set(k, e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          />
        </div>
      ))}

      {planEntry?.changes?.length > 0 && (
        <ul className="cw-url-changes">
          {planEntry.changes.map((c) => (
            <li key={`${c.param}-${c.action}-${c.from}`}>
              <code>{c.param}</code>
              {c.action === 'dropped'
                ? <> removed (<span className="cw-url-was">{c.from}</span>)</>
                : <> <span className="cw-url-was">{c.from}</span> → <b>{c.to}</b></>}
              <span className="cw-url-why"> — {c.reason}</span>
            </li>
          ))}
        </ul>
      )}

      {/* A sitelink with no final URL is SKIPPED at launch, silently. Said here
          because the grid cannot show an absence. */}
      {missingUrl && (
        <div className="cw-url-issue">
          <span className="material-icons">error_outline</span>
          <span>This sitelink has no final URL, so Google will skip it at launch.</span>
        </div>
      )}
      {tooLong.map((c) => (
        <div className="cw-url-issue" key={c.key}>
          <span className="material-icons">error_outline</span>
          <span>
            {`Google caps sitelink ${c.label} at ${c.max} characters and this is ${c.length} `}
            {'— the sitelink is skipped at launch, not truncated, so shorten it here.'}
          </span>
        </div>
      ))}
      {issues.map((issue) => (
        <div className="cw-url-issue" key={issue}>
          <span className="material-icons">error_outline</span>
          <span>{issue}</span>
        </div>
      ))}
    </div>
  );
}

// The campaign's own tracking, edited here because step 2 is where the URLs and
// their resolved result are visible together. Step 1 used to carry these; it
// cannot show what they produce, which is how a template that collided with its
// own sitelink URLs shipped twice.
//
// One block per campaign, not per landing-page row: the template and suffix are
// campaign-wide, so N identical editors would be N ways to write one value.
function EditableCampaignUrls({ campaign, onEdit }) {
  const [draft, setDraft] = useState({
    trackingUrlTemplate: campaign?.tracking_url_template || '',
    finalUrlSuffix: campaign?.final_url_suffix || '',
  });
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (dirty) return;
    setDraft({
      trackingUrlTemplate: campaign?.tracking_url_template || '',
      finalUrlSuffix: campaign?.final_url_suffix || '',
    });
  }, [campaign?.tracking_url_template, campaign?.final_url_suffix, dirty]);

  const set = (k, v) => { setDirty(true); setDraft((d) => ({ ...d, [k]: v })); };
  const commit = () => {
    if (!dirty) return;
    setDirty(false);
    onEdit(draft);
  };

  return (
    <div className="cw-url-row cw-camp-urls">
      <div className="cw-url-head">
        <span className="cw-url-label">Campaign tracking</span>
        <span className="cw-url-usedby">applies to every ad and every sitelink without its own</span>
      </div>
      <div className="cw-url-field">
        <span className="cw-url-key">Tracking template</span>
        <input
          className="cw-url-val cw-url-in"
          value={draft.trackingUrlTemplate}
          placeholder="usually blank — tag with the suffix below"
          onChange={(e) => set('trackingUrlTemplate', e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </div>
      <div className="cw-url-field">
        <span className="cw-url-key">Final URL suffix</span>
        <input
          className="cw-url-val cw-url-in"
          value={draft.finalUrlSuffix}
          placeholder="utm_source=google&utm_medium=cpc&utm_campaign=…"
          onChange={(e) => set('finalUrlSuffix', e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        />
      </div>
      <span className="cw-url-why">
        Clearing the template is how a copy carries none — the suffix then does the
        tagging, and it picks the right connector for each URL.
      </span>
    </div>
  );
}

function UrlRow({ entry, liveCampaign }) {
  const changes = entry.changes || [];
  // Once the campaign's tracking is editable the plan's baked resolution goes
  // stale on the first edit, so re-resolve from the live values when they are
  // available. Same rules, same module as the build (copyUrlPreview).
  const live = liveCampaign
    ? previewLandingUrl(entry.final_url, liveCampaign.tracking_url_template,
      liveCampaign.final_url_suffix)
    : null;
  const issues = live ? live.issues : (entry.issues || []);
  // The displayed LAYERS have to come from the same place as the resolution, or
  // the row contradicts itself: the campaign block above showed the edited
  // template while this row still printed the one the build had recorded, so a
  // single screen named two different templates for one campaign.
  const shownTemplate = live ? liveCampaign.tracking_url_template : entry.tracking_url_template;
  const shownSuffix = live ? liveCampaign.final_url_suffix : entry.final_url_suffix;
  return (
    <div className={`cw-url-row${issues.length ? ' has-issue' : ''}`}>
      <div className="cw-url-head">
        <span className="cw-url-label">{entry.label}</span>
        {entry.used_by?.length > 1 && (
          <span className="cw-url-usedby">{entry.used_by.length} placements</span>
        )}
      </div>

      {/* The layers, shown only when set — an empty tracking template is the
          recommended state, not a gap worth a row saying "none". */}
      {entry.original_final_url && (
        <div className="cw-url-field">
          <span className="cw-url-key">Final URL was</span>
          <code className="cw-url-val cw-url-was">{entry.original_final_url}</code>
        </div>
      )}
      <div className="cw-url-field">
        <span className="cw-url-key">Final URL</span>
        <code className="cw-url-val">{entry.final_url || '—'}</code>
      </div>
      {shownTemplate && (
        <div className="cw-url-field">
          <span className="cw-url-key">Tracking template</span>
          <code className="cw-url-val">{shownTemplate}</code>
        </div>
      )}
      {shownSuffix && (
        <div className="cw-url-field">
          <span className="cw-url-key">Final URL suffix</span>
          <code className="cw-url-val">{shownSuffix}</code>
        </div>
      )}

      {changes.length > 0 && (
        <ul className="cw-url-changes">
          {changes.map((c) => (
            <li key={`${c.param}-${c.action}-${c.from}`}>
              <code>{c.param}</code>
              {c.action === 'dropped' ? (
                <> removed (<span className="cw-url-was">{c.from}</span>)</>
              ) : (
                <> <span className="cw-url-was">{c.from}</span> → <b>{c.to}</b></>
              )}
              <span className="cw-url-why"> — {c.reason}</span>
            </li>
          ))}
        </ul>
      )}

      {issues.map((issue) => (
        <div className="cw-url-issue" key={issue}>
          <span className="material-icons">error_outline</span>
          <span>{issue}</span>
        </div>
      ))}
    </div>
  );
}

export default function CopyUrlTrackingPanel({ plan, launcherResult, onSitelinkOp, onCampaignUrlEdit }) {
  const groups = planGroups(plan);
  // Sitelinks come from the LIVE template so an edit is visible immediately;
  // the plan supplies what the build's identity rewrite did. Joined by campaign
  // NAME because the plan is keyed by SOURCE id while the template is a list —
  // and the duplicate-name gate guarantees names are unique in the account.
  //
  // `campaigns` sits at the TOP of launcherResult, not under a `template` key:
  // the handler builds the payload as `dict(template)` and adds the sidecars
  // beside it (_persist_launcher_payload). Reading launcherResult.template gives
  // undefined, which silently disables editing rather than failing — so this
  // reads the same place applyCopyReviewEdits and the row builders do.
  const campaignsByName = new Map(
    (launcherResult?.campaigns || []).map((c, i) => [c?.name, { campaign: c, index: i }]),
  );
  const editable = typeof onSitelinkOp === 'function';
  // Open when there is something to act on, closed when the URLs are simply
  // correct: a panel that demands attention every time stops being read the
  // time it actually matters.
  //
  // The SITELINK half of the count is recomputed from the live template rather
  // than read off the plan. url_tracking_plan is written once, by the Python
  // copier at build time, and nothing recomputes it — the planning-review PUT
  // just stores the edited template. So the moment a sitelink is edited here the
  // plan's issue_count describes a URL that no longer exists, and the header
  // would still say "6 tracking problems to fix" after all six were fixed.
  //
  // Counted from the COMMITTED template, not from a row's in-progress draft: the
  // header describes the copy as saved, so it settles when an edit commits rather
  // than flickering per keystroke.
  const liveSitelinkIssues = (group) => {
    const match = campaignsByName.get(group.campaign_name);
    const sitelinks = match?.campaign?.sitelinks;
    if (!editable || !Array.isArray(sitelinks)) return null;
    return sitelinks.reduce((n, sl) => {
      const missingUrl = !String(sl?.final_url || '').trim() ? 1 : 0;
      const { issues } = previewLandingUrl(
        sl?.final_url,
        sl?.tracking_url_template || match.campaign.tracking_url_template,
        sl?.final_url_suffix || match.campaign.final_url_suffix,
      );
      return n + issues.length + missingUrl;
    }, 0);
  };
  const totals = groups.reduce(
    (acc, g) => {
      const live = liveSitelinkIssues(g);
      // The plan's count covers campaign rows AND sitelinks; when sitelinks are
      // live we take only the campaign half from it and add the fresh count.
      const planSitelinkIssues = (g.sitelinks || [])
        .reduce((n, e) => n + (e.issues?.length || 0), 0);
      return {
        changes: acc.changes + (g.change_count || 0),
        issues: acc.issues + (g.issue_count || 0)
          + (live === null ? 0 : live - planSitelinkIssues),
      };
    },
    { changes: 0, issues: 0 },
  );
  const [open, setOpen] = useState(totals.issues > 0);
  if (groups.length === 0) return null;

  const summary = [
    totals.issues > 0
      ? `${totals.issues} tracking problem${totals.issues === 1 ? '' : 's'} to fix`
      : null,
    totals.changes > 0
      ? `${totals.changes} parameter${totals.changes === 1 ? '' : 's'} rewritten for this copy`
      : null,
  ].filter(Boolean);

  return (
    <div className="copy-workflow" style={{ display: 'block', height: 'auto', overflow: 'visible' }}>
      <div className={`callout cw-building cw-urlplan${totals.issues ? ' has-issue' : ''}`}>
        <span className="material-icons">link</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <button
            type="button"
            className="cw-urlplan-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            <span className="material-icons">{open ? 'expand_more' : 'chevron_right'}</span>
            <b>Where these clicks will land</b>
            {summary.length > 0 && <span className="cw-urlplan-sub">{summary.join(' · ')}</span>}
          </button>
          {open && groups.map((g) => {
            const match = campaignsByName.get(g.campaign_name);
            const liveSitelinks = match?.campaign?.sitelinks;
            // Fall back to the build-time plan when the template has no sitelinks
            // list to edit (a non-Google copy, or an older build).
            const canEdit = editable && Array.isArray(liveSitelinks);
            const planByText = new Map(
              (g.sitelinks || []).map((e) => [
                String(e.label || '').replace(/^Sitelink\s*[“"]?|[”"]$/g, ''), e]),
            );
            return (
              <div className="cw-urlplan-group" key={g.sourceId}>
                {groups.length > 1 && (
                  <div className="cw-urlplan-campaign">{g.campaign_name}</div>
                )}
                {canEdit && typeof onCampaignUrlEdit === 'function' && (
                  <EditableCampaignUrls
                    campaign={match.campaign}
                    onEdit={(fields) => onCampaignUrlEdit(match.index, fields)}
                  />
                )}
                {(g.campaign || []).map((e) => (
                  <UrlRow entry={e} key={`c-${e.final_url}`}
                    liveCampaign={canEdit ? match.campaign : null} />
                ))}

                {canEdit ? (
                  <>
                    {liveSitelinks.map((sl, i) => (
                      <EditableSitelink
                        key={`${sl?.link_text || 'sitelink'}-${i}`}
                        sitelink={sl}
                        campaignTemplate={match.campaign.tracking_url_template}
                        campaignSuffix={match.campaign.final_url_suffix}
                        planEntry={planByText.get(sl?.link_text)}
                        onOp={(op) => onSitelinkOp(match.index, op)}
                        onRemove={() => onSitelinkOp(match.index,
                          { op: 'remove', value: sl?.link_text })}
                      />
                    ))}
                    <button
                      type="button"
                      className="cw-btn cw-btn-secondary cw-btn-sm cw-sl-add"
                      onClick={() => onSitelinkOp(match.index, {
                        op: 'add',
                        value: `Sitelink ${liveSitelinks.length + 1}`,
                      })}
                    >
                      <span className="material-icons">add</span> Add sitelink
                    </button>
                  </>
                ) : (
                  (g.sitelinks || []).map((e) => <UrlRow entry={e} key={`s-${e.label}`} />)
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
